import React from 'react';

import dayjs from '~/timeInit';

import { Box, Stack, TextField, Typography } from '@mui/material';
import Grid from '@mui/material/Grid';

import {
  type AnyItem,
  type AtolFlightItem,
  type DateValidationError,
  type EditableItem,
  getDateError,
} from './atolEditHelpers';

interface AtolFlightLegsEditorProps {
  editItem: EditableItem;
  dateErrors: Array<DateValidationError>;
  isLoading: boolean;
  onUpdateField: (itemId: string, updater: (current: AnyItem) => AnyItem) => void;
}

export default function AtolFlightLegsEditor({
  editItem,
  dateErrors,
  isLoading,
  onUpdateField,
}: AtolFlightLegsEditorProps) {
  const flightItem = editItem.current as AtolFlightItem;
  const { legs } = flightItem;

  return (
    <Stack spacing={2}>
      <TextField
        label="Airline PNR"
        value={flightItem.airlinePnr ?? ''}
        onChange={(e) => {
          const newValue = e.target.value;
          onUpdateField(flightItem.id, (currentItem) => {
            const clone = JSON.parse(JSON.stringify(currentItem)) as AtolFlightItem;
            clone.airlinePnr = newValue || undefined;
            return clone;
          });
        }}
        fullWidth
        disabled={isLoading}
        size="small"
        helperText="PNR displayed in the certificate flight description"
      />

      {!legs || legs.length === 0 ? (
        <Typography variant="body2" color="text.secondary">
          No leg information available
        </Typography>
      ) : (
        <Stack spacing={1}>
          {legs.map((leg, legIndex) => {
            const legArrivingError = getDateError(dateErrors, flightItem.id, `leg-${leg.legId}-arrivingDate`);

            return (
              <Box key={leg.legId || legIndex} sx={{ pl: 1, borderLeft: '3px solid', borderColor: 'divider' }}>
                <Grid container spacing={2}>
                  <Grid size={{ xs: 12, sm: 4 }}>
                    <Typography variant="caption" color="text.secondary">
                      Route
                    </Typography>
                    <Typography variant="body2">
                      {leg.origin?.cityName ?? ''} ({leg.origin?.airportCode ?? ''}) → {leg.destination?.cityName ?? ''}{' '}
                      ({leg.destination?.airportCode ?? ''})
                    </Typography>
                  </Grid>
                  <Grid size={{ xs: 6, sm: 4 }}>
                    <TextField
                      label="Departing"
                      type="date"
                      value={leg.departingDate ? dayjs(leg.departingDate).format('YYYY-MM-DD') : ''}
                      onChange={(e) => {
                        const newDate = e.target.value;
                        onUpdateField(flightItem.id, (currentItem) => {
                          const clone = JSON.parse(JSON.stringify(currentItem)) as AtolFlightItem;
                          clone.legs[legIndex] = { ...clone.legs[legIndex], departingDate: newDate };
                          return clone;
                        });
                      }}
                      fullWidth
                      disabled={isLoading}
                      slotProps={{ inputLabel: { shrink: true } }}
                      size="small"
                    />
                  </Grid>
                  <Grid size={{ xs: 6, sm: 4 }}>
                    <TextField
                      label="Arriving"
                      type="date"
                      value={leg.arrivingDate ? dayjs(leg.arrivingDate).format('YYYY-MM-DD') : ''}
                      onChange={(e) => {
                        const newDate = e.target.value;
                        onUpdateField(flightItem.id, (currentItem) => {
                          const clone = JSON.parse(JSON.stringify(currentItem)) as AtolFlightItem;
                          clone.legs[legIndex] = { ...clone.legs[legIndex], arrivingDate: newDate };
                          return clone;
                        });
                      }}
                      fullWidth
                      disabled={isLoading}
                      error={!!legArrivingError}
                      helperText={legArrivingError}
                      slotProps={{ inputLabel: { shrink: true } }}
                      size="small"
                    />
                  </Grid>
                </Grid>
              </Box>
            );
          })}
        </Stack>
      )}
    </Stack>
  );
}
